import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Col, Radio, Row, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { IDevice } from '@pages/Assets/Inventory/types';
import { IDeviceActionRequestPayload } from '@components/DeviceActionModal/types';
import ActionTypes from '@utils/deviceActions';
import api from '@services/api';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import StatCard from '@components/StatCard';
import SessionContext from '@providers/Session/SessionContext';

import * as S from './styles';

const { Text } = Typography;

interface IActionSmsBlock {
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  devicesProps: IDevice[] | undefined;
  uploadedDevices: IDevice[] | undefined;
  currentStep: number;
}

type SuspensionTypes =
  | ActionTypes.SUSPENSION
  | ActionTypes.WITHDRAWAL_OF_SUSPENSION
  | ActionTypes.CANCELLATION_SUSPENSION;

const useSuspensionWithdraw = ({
  handleDeviceActions,
  devicesProps,
  uploadedDevices,
  currentStep,
}: IActionSmsBlock) => {
  const { t } = useTranslation(['device-actions-modal']);
  const { isMot } = useContext(SessionContext);
  const [action, setAction] = useState<SuspensionTypes>(ActionTypes.SUSPENSION);
  const [suspensionEligibility, setSuspensionEligibility] = useState<
    | {
        nbSuspendable: number;
        nbNotSuspendable: number;
      }
    | undefined
  >();
  const isInformativeStep =
    (devicesProps && devicesProps?.length > 0 && currentStep === 1) ||
    (uploadedDevices && uploadedDevices?.length > 0 && currentStep === 2);

  const devices = useMemo(
    () =>
      (devicesProps && devicesProps.length > 0
        ? devicesProps
        : uploadedDevices
      )?.map(device => device.msisdn),
    [devicesProps, uploadedDevices],
  );

  const handleSuspensionEligibility = useCallback(async () => {
    const { data } = await api.post(
      '/service-proxy/inventory/MOT/suspension-eligibility',
      devices,
    );
    setSuspensionEligibility(data);
  }, [devices]);

  const eligibilityCheck = useMemo(() => {
    if (isMot && action === ActionTypes.SUSPENSION && isInformativeStep) {
      handleSuspensionEligibility();
    }
    return (
      <div style={{ textAlign: 'center' }}>
        <Spin style={{ margin: 16 }} />
        <p style={{ maxWidth: 200, margin: '0 auto' }}>
          {t('steps.suspension-withdraw.checking-eligibility', {
            count: devices?.length ?? 0,
          })}
        </p>
      </div>
    );
  }, [
    action,
    devices?.length,
    handleSuspensionEligibility,
    isInformativeStep,
    isMot,
    t,
  ]);

  const contentSteps = useMemo(() => {
    const allSteps = [
      {
        title: t('steps.suspension-withdraw.action'),
        content: (
          <S.Container>
            <Radio.Group
              className="action-lock-unlock"
              value={action}
              onChange={({ target: { value } }) => {
                setAction(value);
              }}
            >
              <Radio.Button
                value={ActionTypes.SUSPENSION}
                className="action-lock-unlock-radio"
              >
                <LockOutlined />
                {t('steps.suspension-withdraw.suspend')}
              </Radio.Button>
              <Radio.Button
                value={ActionTypes.WITHDRAWAL_OF_SUSPENSION}
                className="action-lock-unlock-radio"
              >
                <UnlockOutlined />
                {t('steps.suspension-withdraw.withdraw')}
              </Radio.Button>
              <Radio.Button
                value={ActionTypes.CANCELLATION_SUSPENSION}
                className="action-lock-unlock-radio"
              >
                <CloseCircleOutlined />
                {t('steps.suspension-withdraw.cancel')}
              </Radio.Button>
            </Radio.Group>
            {action === ActionTypes.WITHDRAWAL_OF_SUSPENSION && (
              <Alert
                showIcon
                type="info"
                style={{
                  padding: 16,
                  marginTop: 8,
                  width: '100%',
                }}
                description={
                  <Text style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    <Text strong>
                      {t('steps.suspension-withdraw.hours-of-completion', {
                        value: 48,
                      })}
                    </Text>
                    <br />
                    {t('steps.suspension-withdraw.withdrawal-charge')}
                  </Text>
                }
              />
            )}
            {action === ActionTypes.CANCELLATION_SUSPENSION && (
              <Alert
                showIcon
                type="info"
                style={{
                  padding: 16,
                  marginTop: 8,
                  width: '100%',
                }}
                description={
                  <Text style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {t('steps.suspension-withdraw.cancellation-of-suspension')}
                  </Text>
                }
              />
            )}
          </S.Container>
        ),
      },
    ];

    if (action === ActionTypes.SUSPENSION) {
      allSteps.push({
        title: t('steps.suspension-withdraw.informative'),
        content: (
          <S.Container>
            {suspensionEligibility ? (
              <>
                <p
                  style={{
                    marginBottom: 16,
                    textAlign: 'center',
                    opacity: 0.6,
                  }}
                >
                  {t('steps.suspension-withdraw.selected-lines', {
                    count: devices?.length ?? 0,
                  })}
                </p>
                <Row gutter={[16, 16]}>
                  <Col xl={12} xs={24}>
                    <StatCard
                      title={t('steps.suspension-withdraw.eligible', {
                        count: suspensionEligibility?.nbSuspendable ?? 0,
                      })}
                      icon={<CheckCircleOutlined />}
                      value={suspensionEligibility?.nbSuspendable}
                    />
                  </Col>
                  <Col xl={12} xs={24}>
                    <StatCard
                      title={t('steps.suspension-withdraw.non-eligible', {
                        count: suspensionEligibility?.nbNotSuspendable ?? 0,
                      })}
                      icon={<CloseCircleOutlined />}
                      value={suspensionEligibility?.nbNotSuspendable}
                    />
                  </Col>

                  <Alert
                    style={{ padding: 16, marginTop: 8, width: '100%' }}
                    description={
                      <Text style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                        {t('steps.suspension-withdraw.hours-of-completion', {
                          value: 48,
                        })}
                      </Text>
                    }
                    type="info"
                    showIcon
                  />
                </Row>
              </>
            ) : (
              eligibilityCheck
            )}
          </S.Container>
        ),
      });
    }

    return allSteps;
  }, [action, devices?.length, eligibilityCheck, suspensionEligibility, t]);

  const reinitState = () => {
    setAction(ActionTypes.SUSPENSION);
    setSuspensionEligibility(undefined);
  };

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    payload.action = action;

    payload.devices =
      devicesProps && devicesProps?.length > 0
        ? devicesProps?.map(device => {
            return { msisdn: device.msisdn };
          })
        : uploadedDevices;

    return handleDeviceActions(payload);
  }, [handleDeviceActions, action, devicesProps, uploadedDevices]);

  const suspensionWithdrawData = {
    contentSteps,
    onSubmit,
    reinitState,
    extraAction: action,
  };

  return { suspensionWithdrawData };
};

export default useSuspensionWithdraw;
